import React from "react";
import { Code, Brain, School, Award } from "lucide-react";

const About = () => {
  const skills = {
    programming: ["Python", "JavaScript", "Java", "C", "SQL", "Prolog"],
    web: ["React", "Node.js", "Django", "Flask", "Next.js"],
    mlops: ["MLflow", "Airflow", "ZenML", "DVC"],
    tools: ["Docker", "Kubernetes", "Git", "CI/CD"]
  };

  const educationData = [
    {
      degree: "Phd in Electrical Engineering and Computer Science",
      institution: "Wichita State University",
      location: "Wichita, KS, USA",
      period: "August 2024 - Present",
      description: "Currently pursuing graduate studies in Computer Science, focusing on advanced topics in AI and reinforcement learning."
    },
    {
      degree: "Bachelor of Computer Science, Minor in Business Administration",
      institution: "Al Akhawayn University",
      location: "Ifrane, Morocco",
      period: "September 2020 - May 2024",
      description: "Graduated Summa Cum Laude with focus on software engineering and machine learning."
    }
  ];

  const certifications = [
    "AWS Certified Cloud Practitioner",
    "Supervised Machine Learning: Regression and Classification",
    "Convolutional Neural Networks",
    "Neural Networks and Deep Learning"
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">About Me</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            I'm a passionate software engineer with interests in full-stack development, 
            machine learning, and computer vision. I love building innovative solutions 
            to complex problems.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Skills Section */}
          <div className="bg-gray-50 rounded-xl p-8 shadow-md">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Code className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 ml-4">Technical Skills</h3>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-700 mb-2">Programming Languages</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.programming.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm text-gray-700">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-700 mb-2">Web Technologies</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.web.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm text-gray-700">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-700 mb-2">MLOps</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.mlops.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm text-gray-700">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-700 mb-2">Tools & DevOps</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.tools.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm text-gray-700">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Education Section */}
          <div className="bg-gray-50 rounded-xl p-8 shadow-md">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <School className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 ml-4">Education</h3>
            </div>

            <div className="space-y-8">
              {educationData.map((edu, index) => (
                <div key={index} className="border-l-2 border-indigo-200 pl-4">
                  <span className="text-sm font-medium text-indigo-600 block">{edu.period}</span>
                  <h4 className="text-lg font-bold text-gray-800 mt-1">{edu.degree}</h4>
                  <p className="text-gray-600">{edu.institution}, {edu.location}</p>
                  <p className="text-gray-600 mt-2 text-sm">{edu.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Certifications Section */}
          <div className="bg-gray-50 rounded-xl p-8 shadow-md lg:col-span-2">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Award className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 ml-4">Certifications</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {certifications.map((cert, index) => (
                <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <p className="font-medium text-gray-800">{cert}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;